import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { 
  Leaf, 
  ArrowLeft, 
  ArrowRight, 
  Check,
  User,
  Heart,
  Zap,
  Target,
  Settings,
  UserCheck
} from "lucide-react";

const STEPS = [
  { id: "demographics", title: "About You", icon: User, description: "Tell us a bit about yourself" },
  { id: "values", title: "Your Values", icon: Heart, description: "What matters most to you" },
  { id: "skills", title: "Your Skills", icon: Zap, description: "What you're good at" },
  { id: "weaknesses", title: "Growth Areas", icon: Target, description: "Where you'd like to improve" },
  { id: "goals", title: "Your Goals", icon: Target, description: "What you want to achieve" },
  { id: "personality", title: "Assessments", icon: Settings, description: "Your personality profile" },
  { id: "preferences", title: "Coach Preferences", icon: UserCheck, description: "Your ideal coach" },
  { id: "agreement", title: "Agreement", icon: UserCheck, description: "Terms and expectations" }
];

const COACHING_CATEGORIES = [
  "Leadership Resilience",
  "Sabbatical Planning & Re-entry",
  "Church Staffing & Team Leadership",
  "Succession & Transition Planning",
  "Church Planting & Launch",
  "Spiritual Formation & Soul Care",
  "Conflict Resolution & Reconciliation",
  "Vision Casting & Strategic Planning",
  "Non-Profit Leadership & Governance",
  "Fundraising & Stewardship",
  "Multisite & Church Growth",
  "Revitalization & Turnaround",
  "Bi-vocational Ministry",
  "Cross-Cultural & Missions Leadership"
];

const CORE_VALUES = [
  "Integrity", "Faithfulness", "Servanthood", "Humility", "Grace",
  "Compassion", "Wisdom", "Courage", "Authenticity", "Excellence",
  "Community", "Discipleship", "Evangelism", "Justice", "Stewardship"
];

const SKILLS_LIST = [
  "Preaching & Teaching", "Pastoral Care", "Leadership Development", "Team Building",
  "Strategic Planning", "Conflict Resolution", "Fundraising", "Administration",
  "Mentoring & Discipleship", "Community Outreach", "Worship Leading", "Counseling",
  "Writing & Communication", "Board Relations", "Change Management"
];

const COACHING_STYLES = [
  "Supportive & Encouraging",
  "Direct & Challenging",
  "Structured & Goal-Oriented",
  "Intuitive & Exploratory",
  "Analytical & Data-Driven"
];

const ENNEAGRAM_TYPES = [
  { value: "1", label: "Type 1 - The Reformer" },
  { value: "2", label: "Type 2 - The Helper" },
  { value: "3", label: "Type 3 - The Achiever" },
  { value: "4", label: "Type 4 - The Individualist" },
  { value: "5", label: "Type 5 - The Investigator" },
  { value: "6", label: "Type 6 - The Loyalist" },
  { value: "7", label: "Type 7 - The Enthusiast" },
  { value: "8", label: "Type 8 - The Challenger" },
  { value: "9", label: "Type 9 - The Peacemaker" }
];

const MYERS_BRIGGS_TYPES = [
  "INTJ", "INTP", "ENTJ", "ENTP",
  "INFJ", "INFP", "ENFJ", "ENFP",
  "ISTJ", "ISFJ", "ESTJ", "ESFJ",
  "ISTP", "ISFP", "ESTP", "ESFP"
];

const STRENGTHS_FINDER = [
  "Achiever", "Activator", "Adaptability", "Analytical", "Arranger",
  "Belief", "Command", "Communication", "Competition", "Connectedness",
  "Consistency", "Context", "Deliberative", "Developer", "Discipline",
  "Empathy", "Focus", "Futuristic", "Harmony", "Ideation",
  "Includer", "Individualization", "Input", "Intellection", "Learner",
  "Maximizer", "Positivity", "Relator", "Responsibility", "Restorative",
  "Self-Assurance", "Significance", "Strategic", "Woo"
];

const PRIMAL_QUESTIONS = [
  { value: "Am I safe?", label: "Am I safe?" },
  { value: "Am I loved?", label: "Am I loved?" },
  { value: "Am I enough?", label: "Am I enough?" },
  { value: "Do I belong?", label: "Do I belong?" },
  { value: "Am I in control?", label: "Am I in control?" }
];

const SurveyPage = () => {
  const navigate = useNavigate();
  const { token, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const [surveyData, setSurveyData] = useState({
    demographics: {
      age_range: "",
      gender: "",
      location: "",
      profession: "",
      industry: ""
    },
    values: {
      core_values: [],
      work_values: [],
      life_priorities: []
    },
    skills: {
      strong_skills: [],
      developing_skills: []
    },
    weaknesses: {
      challenge_areas: [],
      improvement_goals: []
    },
    goals: {
      coaching_categories: [],
      specific_goals: "",
      timeline: ""
    },
    personality: {
      enneagram: "",
      myers_briggs: "",
      strengths_finder: [],
      primal_question: ""
    },
    preferences: {
      gender_preference: "",
      experience_level: "",
      coaching_style: [],
      communication_preference: [],
      availability: ""
    },
    agreement: {
      understands_free_consultation: false,
      agrees_liability_waiver: false
    }
  });

  const updateSurveyData = (section, field, value) => {
    setSurveyData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const toggleArrayValue = (section, field, value) => {
    setSurveyData(prev => {
      const current = prev[section][field] || [];
      const updated = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: updated
        }
      };
    });
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!surveyData.agreement?.understands_free_consultation || !surveyData.agreement?.agrees_liability_waiver) {
      toast.error("Please agree to all terms before completing your profile");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/survey`,
        { survey_data: surveyData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update user context
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateUser(userResponse.data);
      
      toast.success("Survey completed! View your matches.");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to save survey. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;

  const renderStep = () => {
    const stepVariants = {
      enter: { x: 50, opacity: 0 },
      center: { x: 0, opacity: 1 },
      exit: { x: -50, opacity: 0 }
    };

    switch (currentStep) {
      case 0: // Demographics
        return (
          <motion.div
            key="demographics"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Age Range</Label>
                <Select
                  value={surveyData.demographics.age_range}
                  onValueChange={(v) => updateSurveyData("demographics", "age_range", v)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="age-range-select">
                    <SelectValue placeholder="Select age range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="18-24">18-24</SelectItem>
                    <SelectItem value="25-34">25-34</SelectItem>
                    <SelectItem value="35-44">35-44</SelectItem>
                    <SelectItem value="45-54">45-54</SelectItem>
                    <SelectItem value="55+">55+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Gender</Label>
                <Select
                  value={surveyData.demographics.gender}
                  onValueChange={(v) => updateSurveyData("demographics", "gender", v)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Location</Label>
              <Input
                placeholder="City, State/Country"
                value={surveyData.demographics.location}
                onChange={(e) => updateSurveyData("demographics", "location", e.target.value)}
                className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                data-testid="location-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Current Role</Label>
                <Input
                  placeholder="e.g., Senior Pastor, Executive Director"
                  value={surveyData.demographics.profession}
                  onChange={(e) => updateSurveyData("demographics", "profession", e.target.value)}
                  className="h-12 rounded-xl border-stone-200 bg-stone-50/50"
                  data-testid="profession-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Ministry/Organization Type</Label>
                <Select
                  value={surveyData.demographics.industry}
                  onValueChange={(v) => updateSurveyData("demographics", "industry", v)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="industry-select">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Church / Ministry">Church / Ministry</SelectItem>
                    <SelectItem value="Non-Profit Organization">Non-Profit Organization</SelectItem>
                    <SelectItem value="Parachurch Ministry">Parachurch Ministry</SelectItem>
                    <SelectItem value="Christian Education">Christian Education</SelectItem>
                    <SelectItem value="Missions Organization">Missions Organization</SelectItem>
                    <SelectItem value="Denominational Leadership">Denominational Leadership</SelectItem>
                    <SelectItem value="Marketplace / Bi-vocational">Marketplace / Bi-vocational</SelectItem>
                    <SelectItem value="Seminary / Training">Seminary / Training</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </motion.div>
        );

      case 1: // Values
        return (
          <motion.div
            key="values"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                Select your core values (choose 3-5)
              </Label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {CORE_VALUES.map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleArrayValue("values", "core_values", value)}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      surveyData.values.core_values.includes(value)
                        ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                    }`}
                    data-testid={`value-${value.toLowerCase()}`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                What do you value most at work? (choose 2-3)
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {["Collaboration", "Autonomy", "Innovation", "Stability", "Recognition", "Impact"].map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => toggleArrayValue("values", "work_values", value)}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      surveyData.values.work_values.includes(value)
                        ? "bg-[#E09F7D] text-white border-[#E09F7D]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#E09F7D]/50"
                    }`}
                    data-testid={`work-value-${value.toLowerCase()}`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        );

      case 2: // Skills
        return (
          <motion.div
            key="skills"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                What are your strongest skills? (choose 3-5)
              </Label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {SKILLS_LIST.map((skill) => (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => toggleArrayValue("skills", "strong_skills", skill)}
                    className={`p-2 md:p-3 rounded-xl border text-xs md:text-sm font-medium transition-all text-center leading-tight hyphens-auto break-words ${
                      surveyData.skills.strong_skills.includes(skill)
                        ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                    }`}
                    data-testid={`strong-skill-${skill.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {skill}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                Which skills are you actively developing? (choose 2-4)
              </Label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {SKILLS_LIST.map((skill) => (
                  <button
                    key={skill}
                    type="button"
                    onClick={() => toggleArrayValue("skills", "developing_skills", skill)}
                    className={`p-2 md:p-3 rounded-xl border text-xs md:text-sm font-medium transition-all text-center leading-tight hyphens-auto break-words ${
                      surveyData.skills.developing_skills.includes(skill)
                        ? "bg-[#E09F7D] text-white border-[#E09F7D]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#E09F7D]/50"
                    }`}
                    data-testid={`developing-skill-${skill.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {skill}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        );

      case 3: // Weaknesses
        return (
          <motion.div
            key="weaknesses"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                What areas challenge you the most? (choose 2-4)
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  "Burnout & Fatigue", "Work-Life Balance", "Delegation", 
                  "Difficult Conversations", "Staff Conflict", "Board Relations",
                  "Loneliness in Leadership", "Decision Fatigue", "Vision Clarity",
                  "Financial Pressure", "Succession Planning", "Personal Spiritual Health"
                ].map((area) => (
                  <button
                    key={area}
                    type="button"
                    onClick={() => toggleArrayValue("weaknesses", "challenge_areas", area)}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      surveyData.weaknesses.challenge_areas.includes(area)
                        ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                    }`}
                    data-testid={`challenge-${area.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {area}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                What would you like to improve? (optional)
              </Label>
              <Textarea
                placeholder="Describe specific areas you'd like to work on..."
                value={surveyData.weaknesses.improvement_goals.join("\n")}
                onChange={(e) => updateSurveyData("weaknesses", "improvement_goals", e.target.value.split("\n").filter(Boolean))}
                className="min-h-[120px] rounded-xl border-stone-200 bg-stone-50/50"
                data-testid="improvement-goals-textarea"
              />
            </div>
          </motion.div>
        );

      case 4: // Goals
        return (
          <motion.div
            key="goals"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                What type of coaching are you seeking? (choose all that apply)
              </Label>
              <div className="grid grid-cols-2 gap-3">
                {COACHING_CATEGORIES.map((category) => (
                  <button
                    key={category}
                    type="button"
                    onClick={() => toggleArrayValue("goals", "coaching_categories", category)}
                    className={`p-4 rounded-xl border text-left font-medium transition-all ${
                      surveyData.goals.coaching_categories.includes(category)
                        ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                    }`}
                    data-testid={`category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                Describe your specific goals
              </Label>
              <Textarea
                placeholder="What do you hope to achieve through coaching?"
                value={surveyData.goals.specific_goals}
                onChange={(e) => updateSurveyData("goals", "specific_goals", e.target.value)}
                className="min-h-[120px] rounded-xl border-stone-200 bg-stone-50/50"
                data-testid="specific-goals-textarea"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Timeline for Achieving Goals</Label>
              <Select
                value={surveyData.goals.timeline}
                onValueChange={(v) => updateSurveyData("goals", "timeline", v)}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="timeline-select">
                  <SelectValue placeholder="Select timeline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-3 months">1-3 months</SelectItem>
                  <SelectItem value="3-6 months">3-6 months</SelectItem>
                  <SelectItem value="6-12 months">6-12 months</SelectItem>
                  <SelectItem value="1+ year">1+ year</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </motion.div>
        );

      case 5: // Personality Assessments
        return (
          <motion.div
            key="personality"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <p className="text-[#78716C] text-sm mb-4">
              If you've taken any of these assessments, sharing your results helps us find coaches who understand your personality type. All fields are optional.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Enneagram Type</Label>
                <Select
                  value={surveyData.personality.enneagram}
                  onValueChange={(v) => updateSurveyData("personality", "enneagram", v)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="enneagram-select">
                    <SelectValue placeholder="Select your type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unknown">I don't know</SelectItem>
                    {ENNEAGRAM_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Myers-Briggs Type</Label>
                <Select
                  value={surveyData.personality.myers_briggs}
                  onValueChange={(v) => updateSurveyData("personality", "myers_briggs", v)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="myers-briggs-select">
                    <SelectValue placeholder="Select your type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unknown">I don't know</SelectItem>
                    {MYERS_BRIGGS_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                StrengthsFinder Top 5 (select up to 5)
              </Label>
              <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                {STRENGTHS_FINDER.map((strength) => (
                  <button
                    key={strength}
                    type="button"
                    onClick={() => {
                      const current = surveyData.personality.strengths_finder || [];
                      if (current.includes(strength)) {
                        updateSurveyData("personality", "strengths_finder", current.filter(s => s !== strength));
                      } else if (current.length < 5) {
                        updateSurveyData("personality", "strengths_finder", [...current, strength]);
                      }
                    }}
                    className={`p-2 rounded-lg border text-xs font-medium transition-all ${
                      surveyData.personality.strengths_finder?.includes(strength)
                        ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                    }`}
                    data-testid={`strength-${strength.toLowerCase()}`}
                  >
                    {strength}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Primal Question</Label>
              <Select
                value={surveyData.personality.primal_question}
                onValueChange={(v) => updateSurveyData("personality", "primal_question", v)}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="primal-question-select">
                  <SelectValue placeholder="Select your primal question" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unknown">I don't know</SelectItem>
                  {PRIMAL_QUESTIONS.map((pq) => (
                    <SelectItem key={pq.value} value={pq.value}>{pq.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#78716C] mt-1">
                Your primal question is the core question that drives your deepest motivations and fears.
              </p>
            </div>
          </motion.div>
        );

      case 6: // Preferences
        return (
          <motion.div
            key="preferences"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-8"
          >
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Coach Gender Preference</Label>
                <Select
                  value={surveyData.preferences.gender_preference}
                  onValueChange={(v) => updateSurveyData("preferences", "gender_preference", v)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="coach-gender-select">
                    <SelectValue placeholder="Select preference" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="No preference">No preference</SelectItem>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[#2C332B] font-medium">Coach Experience Level</Label>
                <Select
                  value={surveyData.preferences.experience_level}
                  onValueChange={(v) => updateSurveyData("preferences", "experience_level", v)}
                >
                  <SelectTrigger className="h-12 rounded-xl" data-testid="experience-level-select">
                    <SelectValue placeholder="Select experience level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Any">Any</SelectItem>
                    <SelectItem value="Entry (1-3 years)">Entry (1-3 years)</SelectItem>
                    <SelectItem value="Mid (4-7 years)">Mid (4-7 years)</SelectItem>
                    <SelectItem value="Senior (8+ years)">Senior (8+ years)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                Preferred Coaching Style (choose up to 2)
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {COACHING_STYLES.map((style) => (
                  <button
                    key={style}
                    type="button"
                    onClick={() => toggleArrayValue("preferences", "coaching_style", style)}
                    className={`p-4 rounded-xl border text-left font-medium transition-all ${
                      surveyData.preferences.coaching_style.includes(style)
                        ? "bg-[#7C9A92] text-white border-[#7C9A92]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#7C9A92]/50"
                    }`}
                    data-testid={`style-${style.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
                  >
                    {style}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <Label className="text-[#2C332B] font-medium text-lg">
                Communication Preference
              </Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {["Video Call", "Phone Call", "In Person", "Chat/Email"].map((comm) => (
                  <button
                    key={comm}
                    type="button"
                    onClick={() => toggleArrayValue("preferences", "communication_preference", comm)}
                    className={`p-3 rounded-xl border text-sm font-medium transition-all ${
                      surveyData.preferences.communication_preference.includes(comm)
                        ? "bg-[#E09F7D] text-white border-[#E09F7D]"
                        : "bg-white text-[#2C332B] border-stone-200 hover:border-[#E09F7D]/50"
                    }`}
                    data-testid={`comm-${comm.toLowerCase().replace(/[\s\/]+/g, '-')}`}
                  >
                    {comm}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#2C332B] font-medium">Preferred Session Availability</Label>
              <Select
                value={surveyData.preferences.availability}
                onValueChange={(v) => updateSurveyData("preferences", "availability", v)}
              >
                <SelectTrigger className="h-12 rounded-xl" data-testid="availability-select">
                  <SelectValue placeholder="Select availability" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Weekday mornings">Weekday mornings</SelectItem>
                  <SelectItem value="Weekday afternoons">Weekday afternoons</SelectItem>
                  <SelectItem value="Weekday evenings">Weekday evenings</SelectItem>
                  <SelectItem value="Weekends">Weekends</SelectItem>
                  <SelectItem value="Flexible">Flexible</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </motion.div>
        );

      case 7: // Agreement
        return (
          <motion.div
            key="agreement"
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="p-4 bg-[#F5F1E8] rounded-xl">
              <p className="text-sm text-[#2C332B]">
                Please review the following information before completing your profile.
              </p>
            </div>

            {/* Free Consultation Info */}
            <div className="p-5 border border-[#7C9A92]/30 bg-[#7C9A92]/5 rounded-xl space-y-3">
              <h3 className="font-semibold text-[#2C332B]">Free 15-Minute Consultation</h3>
              <p className="text-sm text-[#78716C]">
                All CoachBridge coaches have agreed to provide a <strong>FREE 15-minute phone or video consultation</strong> to help you determine if they are a good match before committing to paid coaching sessions. This allows you to get to know your potential coach and ask questions before moving forward.
              </p>
              <label className="flex items-start gap-3 cursor-pointer mt-4">
                <input
                  type="checkbox"
                  checked={surveyData.agreement?.understands_free_consultation || false}
                  onChange={(e) => updateSurveyData("agreement", "understands_free_consultation", e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-stone-300 text-[#7C9A92] focus:ring-[#7C9A92]"
                  data-testid="understand-consultation"
                />
                <span className="text-sm text-[#2C332B]">
                  I understand that matched coaches will provide a free 15-minute consultation to verify a good match.
                </span>
              </label>
            </div>

            {/* Liability Waiver */}
            <div className="p-5 border border-stone-200 rounded-xl space-y-3">
              <h3 className="font-semibold text-[#2C332B]">Liability Release & Waiver</h3>
              <div className="text-sm text-[#78716C] space-y-2 max-h-48 overflow-y-auto p-3 bg-stone-50 rounded-lg">
                <p>
                  By checking the box below, I acknowledge and agree to the following:
                </p>
                <p>
                  <strong>RELEASE OF LIABILITY:</strong> I hereby release, waive, discharge, and covenant not to sue CoachBridge, its officers, directors, employees, agents, and affiliates from any and all liability, claims, demands, actions, or causes of action whatsoever arising out of or related to any loss, damage, or injury that may be sustained by me, arising from or in connection with my use of the CoachBridge platform or any coaching services received through or facilitated by the platform.
                </p>
                <p>
                  <strong>INDEPENDENT RELATIONSHIP:</strong> I understand that coaches on CoachBridge are independent contractors and not employees or agents of CoachBridge. CoachBridge serves only as a platform to connect clients with coaches and does not provide coaching services directly.
                </p>
                <p>
                  <strong>COACHING VS. THERAPY:</strong> I understand that coaching is not therapy, counseling, or psychological treatment. If I am experiencing mental health issues, I will seek appropriate professional help. Coaches may recommend I seek additional support if they believe it would be beneficial.
                </p>
                <p>
                  <strong>PAYMENT:</strong> I understand that all payment arrangements are made directly between myself and my coach. CoachBridge does not process payments or guarantee any coaching outcomes.
                </p>
                <p>
                  <strong>CONFIDENTIALITY:</strong> While coaches are expected to maintain confidentiality, I understand that CoachBridge cannot guarantee the actions of independent coaches and is not liable for any breach of confidentiality.
                </p>
              </div>
              <label className="flex items-start gap-3 cursor-pointer mt-4">
                <input
                  type="checkbox"
                  checked={surveyData.agreement?.agrees_liability_waiver || false}
                  onChange={(e) => updateSurveyData("agreement", "agrees_liability_waiver", e.target.checked)}
                  className="w-5 h-5 mt-0.5 rounded border-stone-300 text-[#7C9A92] focus:ring-[#7C9A92]"
                  data-testid="agree-client-waiver"
                />
                <span className="text-sm text-[#2C332B]">
                  I have read, understand, and agree to the Liability Release & Waiver terms above.
                </span>
              </label>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf className="w-6 h-6 text-[#7C9A92]" />
            <span className="font-serif text-xl font-medium text-[#2C332B]">CoachBridge</span>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="text-[#78716C]"
            data-testid="skip-survey-btn"
          >
            Skip for now
          </Button>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="sticky top-[72px] z-40 bg-[#FAFAF9] py-2">
        <div className="max-w-4xl mx-auto px-6">
          <Progress value={progress} className="h-2 bg-stone-200" />
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-12">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {STEPS.map((step, index) => (
            <div
              key={step.id}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentStep
                  ? "bg-[#7C9A92] scale-125"
                  : index < currentStep
                  ? "bg-[#7C9A92]"
                  : "bg-stone-200"
              }`}
            />
          ))}
        </div>

        {/* Step Title */}
        <motion.div
          key={`title-${currentStep}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#7C9A92]/10 flex items-center justify-center mx-auto mb-4">
            {(() => {
              const Icon = STEPS[currentStep].icon;
              return <Icon className="w-8 h-8 text-[#7C9A92]" />;
            })()}
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-light text-[#2C332B] mb-2">
            {STEPS[currentStep].title}
          </h1>
          <p className="text-[#78716C]">{STEPS[currentStep].description}</p>
        </motion.div>

        {/* Form Content */}
        <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm min-h-[400px]">
          <AnimatePresence mode="wait">
            {renderStep()}
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={currentStep === 0}
            className="rounded-full px-6 py-5 border-stone-200"
            data-testid="prev-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>

          {currentStep === STEPS.length - 1 ? (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="rounded-full px-8 py-5 bg-[#7C9A92] hover:bg-[#6B8A82] text-white"
              data-testid="submit-survey-btn"
            >
              {loading ? "Saving..." : "Complete Survey"}
              <Check className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="rounded-full px-8 py-5 bg-[#7C9A92] hover:bg-[#6B8A82] text-white"
              data-testid="next-btn"
            >
              Next
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </main>
    </div>
  );
};

export default SurveyPage;
